import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Loader } from 'semantic-ui-react';
import Dropzone from 'react-dropzone';
import './FileAttachment.scss';
import { deepClone } from '../../../helpers/utility';
import { uploadFileFromBrowser } from '../../../helpers/FileUtility';
import { useActions } from '../../../hooks/useActions';
import { getSharedFormImageUrl } from '../../actions';
import { flattenFileTypes, predefinedTypes } from '../../../templateeditor/filetypes';
import { showConfirm } from '../../../helpers/MessageUtility';
import { analyticsTrack } from '../../../helpers/AnalyticsUtility';
import { SasToken } from 'gf-types';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import { FileAttachment, ValueChangedForFieldFunc } from '../../formeditor-types';
import { DownloadIcon } from '../../../icons/icons';
import { SendAFormInteractive } from '../SendAFormInteractiveState';

export type FileAttachmentProps = {
  disabled?: boolean;
  value?: string;
  valueUpdated?: ValueChangedForFieldFunc;
  onClose?: AnyFunc;
  maxFileSize?: number;
  maxFileCount?: number;
  allowedFileTypes?: string;
  editable?: boolean;
  view?: string;
  templateId?: string;
  templateName?: string;
  formId?: string;
  formName?: string;
  fieldId?: string;
  name?: string;
  compact?: boolean;
} & SendAFormInteractive;

export default function FileAttachment(props: FileAttachmentProps) {
  const { maxFileSize, maxFileCount, allowedFileTypes = '', editable, formId, fieldId, disabled = false } = props;
  const [currentFileAttachments, setCurrentFileAttachments] = useValueUpdatedReducer(props.value, props.valueUpdated);
  const [pendingFileAttachments, setPendingFileAttachments] = useReducer(fileAttachmentsReducer, []);

  const isPublicForm = !!useSelector<TypeMePlease>((state) => state.formeditor.sharedAccessToken);
  const getSharedFormImageUrlAction = useActions(getSharedFormImageUrl);

  const startFileUploads = (files) => {
    const pendingFiles = [];
    for (const file of files) {
      let index = -1;
      for (let i = 0; i < maxFileCount; i++) {
        if (
          !pendingFileAttachments.some((x) => x.index === i) &&
          !currentFileAttachments.some((x) => x.index === i) &&
          !pendingFiles.some((x) => x.index === i)
        ) {
          index = i;
          break;
        }
      }
      if (index > -1) {
        pendingFiles.push({ file, index });
      }
    }

    for (const { file, index } of pendingFiles) {
      const fileAttachment: FileAttachment = {
        index,
        name: file.name,
        size: file.size,
        type: file.type,
        timeStamp: new Date().toISOString(),
        fileId: undefined
      };

      setPendingFileAttachments({ type: 'add', fileAttachment });
      uploadFileFromBrowser(file, false, file.name, null, formId, null, null, null, 0, true, `${fieldId}_${index}`, true).then((uploadResult) => {
        fileAttachment.fileId = uploadResult.fileId;

        if (isPublicForm) {
          getSharedFormImageUrlAction(uploadResult.fileId);
        }

        setPendingFileAttachments({ type: 'remove', fileAttachment });
        setCurrentFileAttachments({ type: 'add', fileAttachment });
      });

      analyticsTrack('File Attachment Uploaded', {
        view: props.view == 'list' ? 'List View' : 'Form View',
        templateId: props.templateId,
        templateName: props.templateName,
        formId: props.formId,
        formName: props.formName,
        fieldId: props.fieldId,
        fieldName: props.name,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });
    }
  };

  const removeUploadedFile = (index) => {
    const fileAttachment = currentFileAttachments.find((x) => x.index === index);
    showConfirm(
      {
        title: 'Confirm',
        msg: `Are you sure you want to delete "${fileAttachment.name}"?`
      },
      (result) => {
        if (result) {
          setCurrentFileAttachments({ type: 'remove', fileAttachment });
        }
      }
    );

    analyticsTrack('Attachment Removed', {
      view: props.view == 'list' ? 'List View' : 'Form View',
      templateId: props.templateId,
      templateName: props.templateName,
      formId: props.formId,
      formName: props.formName,
      fieldId: props.fieldId,
      fieldName: props.name,
      fileName: fileAttachment.name,
      fileType: fileAttachment.type,
      fileSize: fileAttachment.size
    });
  };

  const removePendingFile = (fileAttachment) => {
    setPendingFileAttachments({ type: 'remove', fileAttachment });
  };

  return (
    <div className="file-attachment">
      {props.editable && props.interactive ? (
        <UploadFile
          disabled={disabled}
          maxFileSize={maxFileSize}
          allowedFileTypes={allowedFileTypes}
          maxFileCount={maxFileCount}
          startFileUploads={startFileUploads}
          currentFileAttachments={currentFileAttachments}
          compact={props.compact || currentFileAttachments.length > 0 || pendingFileAttachments.length > 0}
          view={props.view}
          templateId={props.templateId}
          templateName={props.templateName}
          formId={props.formId}
          formName={props.formName}
          fieldId={props.fieldId}
          name={props.name}
        />
      ) : null}
      {currentFileAttachments.length > 0 || pendingFileAttachments.length > 0 ? (
        <FileList
          pendingFileAttachments={pendingFileAttachments}
          currentFileAttachments={currentFileAttachments}
          removeUploadedFile={removeUploadedFile}
          removePendingFile={removePendingFile}
          editable={editable}
          view={props.view}
          templateId={props.templateId}
          templateName={props.templateName}
          formId={props.formId}
          formName={props.formName}
          fieldId={props.fieldId}
          name={props.name}
        />
      ) : null}
    </div>
  );
}

function UploadFile(props) {
  const [uploadError, setUploadError] = useState(false);
  const [uploadErrorText, setUploadErrorText] = useState('');
  const dropzoneRef = useRef<TypeMePlease>();

  const maxFileSizeInBytes = props.maxFileSize * 1024 ** 2;
  // take our allowed types, which might contain categories and then
  // flatten the categories into what our supported types are for them
  const flattenedAllowedTypes = flattenFileTypes(props.allowedFileTypes.split(','));

  const fileAccepted = async (files) => {
    setUploadError(false);
    setUploadErrorText('');
    if (files.length + props.currentFileAttachments.length > props.maxFileCount) {
      setUploadError(true);
      setUploadErrorText(`Maximum number of files you can upload for this field is ${props.maxFileCount}`);
      analyticsTrack('Error Triggered: Max Number of Files Reached', {
        view: props.view == 'list' ? 'List View' : 'Form View',
        templateId: props.templateId,
        templateName: props.templateName,
        formId: props.formId,
        formName: props.formName,
        fieldId: props.fieldId,
        fieldName: props.name,
        fileCount: files.length + props.currentFileAttachments.length,
        maxfilesize: maxFileSizeInBytes,
        maxfilesnumber: props.maxFileCount,
        filetypesallowed: props.allowedFileTypes
      });
      return;
    }
    props.startFileUploads(files);
  };

  const fileRejected = async (files) => {
    console.log(`rejected ${files}`);
    files.forEach((file) => {
      if (file.size > maxFileSizeInBytes) {
        setUploadError(true);
        setUploadErrorText(`The file you selected is ${readableFileSize(file.size)}, which is larger than ${props.maxFileSize}MB`);
        analyticsTrack('Error Triggered: File Size Exceeds Limit', {
          view: props.view == 'list' ? 'List View' : 'Form View',
          templateId: props.templateId,
          templateName: props.templateName,
          formId: props.formId,
          formName: props.formName,
          fieldId: props.fieldId,
          fieldName: props.name,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          maxfilesize: maxFileSizeInBytes,
          maxfilesnumber: props.maxFileCount,
          filetypesallowed: props.allowedFileTypes
        });
      } else {
        setUploadError(true);
        setUploadErrorText('Invalid file type');
        analyticsTrack('Error Triggered: File Type Not Allowed', {
          view: props.view == 'list' ? 'List View' : 'Form View',
          templateId: props.templateId,
          templateName: props.templateName,
          formId: props.formId,
          formName: props.formName,
          fieldId: props.fieldId,
          fieldName: props.name,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          maxfilesize: maxFileSizeInBytes,
          maxfilesnumber: props.maxFileCount,
          filetypesallowed: props.allowedFileTypes
        });
      }
    });
  };

  const openFilePicker = () => {
    dropzoneRef.current.open();
  };

  /** @param {string} types Comma-delimited file type string */
  const formatFileTypes = (types) => {
    if (!types) {
      return 'All file types';
    }

    return (
      types
        .split(',')
        // look up the display name for any predefined types
        .map((type) => predefinedTypes.get(type) || type)
        .join(', ')
    );
  };

  return (
    <React.Fragment>
      <div className="dropzone-file-types">Max Files Allowed: {props.maxFileCount}</div>
      <div className="dropzone-max-files">File Types Accepted: {formatFileTypes(props.allowedFileTypes)}</div>
      {props.maxFileCount === props.currentFileAttachments.length ? null : (
        <Dropzone
          name="file-upload"
          disableClick
          disabled={props.disabled}
          maxSize={maxFileSizeInBytes}
          multiple={true}
          style={{}}
          className={`dropzone ${props.compact ? 'compact' : 'full'}`}
          activeClassName="active"
          rejectClassName="reject"
          disabledClassName="disabled"
          ref={dropzoneRef}
          onDropAccepted={fileAccepted}
          onDropRejected={fileRejected}
          // only set the accept property when there are file types to filter
          {...{ accept: flattenedAllowedTypes.length ? flattenedAllowedTypes : undefined }}
        >
          <i className="icon cloud upload upload-icon" />
          <div className="upload-title">Drag and drop or select from your computer</div>
          <Button disabled={props.disabled} className="select-file secondary browse-button" onClick={openFilePicker} content="Browse" />
        </Dropzone>
      )}
      {uploadError ? <div className="error-text">Error: {uploadErrorText}</div> : null}
    </React.Fragment>
  );
}

export interface FileListProps {
  pendingFileAttachments: FileAttachment[];
  currentFileAttachments: FileAttachment[];
  removePendingFile: (FileAttachment) => void;
  removeUploadedFile: (number) => void;
  editable: boolean;
  view: string;
  templateId: string;
  templateName: string;
  formId: string;
  formName: string;
  fieldId: string;
  name: string;
}

function FileList(props: FileListProps) {
  const combinedFileList = [
    ...props.pendingFileAttachments.map((fileAttachment) => {
      return {
        timeStamp: fileAttachment.timeStamp,
        c: (
          <FileItem
            fileAttachment={fileAttachment}
            key={`pending_${fileAttachment.index}`}
            pending
            onRemove={props.removePendingFile}
            editable={props.editable}
            view={props.view}
            templateId={props.templateId}
            templateName={props.templateName}
            formId={props.formId}
            formName={props.formName}
            fieldId={props.fieldId}
            name={props.name}
          />
        )
      };
    }),
    ...props.currentFileAttachments.map((fileAttachment) => {
      return {
        timeStamp: fileAttachment.timeStamp,
        c: (
          <FileItem
            fileAttachment={fileAttachment}
            key={`current_${fileAttachment.index}`}
            current
            onRemove={props.removeUploadedFile}
            editable={props.editable}
            view={props.view}
            templateId={props.templateId}
            templateName={props.templateName}
            formId={props.formId}
            formName={props.formName}
            fieldId={props.fieldId}
            name={props.name}
          />
        )
      };
    })
  ];

  const list = [...combinedFileList].sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1)).map((x) => x.c);

  return <div className="file-list">{list}</div>;
}

export interface FileItemProps {
  fileAttachment: FileAttachment;
  onRemove: (number) => void;
  pending?: boolean;
  current?: boolean;
  editable: boolean;
  view: string;
  templateId: string;
  templateName: string;
  formId: string;
  formName: string;
  fieldId: string;
  name: string;
}

function FileItem(props: FileItemProps) {
  const sasToken = useAppSelector<SasToken>((state) => state.shared.sasToken);
  const fileUrls = useAppSelector<Record<string, string>>((state) => state.formeditor.fileUrls);

  const getFilePath = (fileId) => {
    if (fileUrls) {
      return fileUrls[fileId];
    }
    if (sasToken) {
      return `${sasToken.baseUrl}${fileId}${sasToken.token}`;
    }
    return null;
  };

  return (
    <div className="file-item">
      <div className="file-name-size">
        <div className="file-name">{props.fileAttachment.name}</div>
        <div className="file-size">{readableFileSize(props.fileAttachment.size)}</div>
      </div>
      {props.pending ? <Loader active inline className="uploading-icon" size="tiny" /> : null}
      {props.current ? (
        <a
          className="download-link"
          href={getFilePath(props.fileAttachment.fileId)}
          target="_blank"
          onClick={() =>
            analyticsTrack('Attachment Accessed', {
              view: props.view == 'list' ? 'List View' : 'Form View',
              templateId: props.templateId,
              templateName: props.templateName,
              formId: props.formId,
              formName: props.formName,
              fieldId: props.fieldId,
              fieldName: props.name,
              fileName: props.fileAttachment.name,
              fileType: props.fileAttachment.type,
              fileSize: props.fileAttachment.size
            })
          }
        >
          <DownloadIcon />
        </a>
      ) : null}
      {props.editable && props.current ? (
        <button className="delete-button" onClick={() => props.onRemove(props.fileAttachment.index)}>
          <DeleteIcon />
        </button>
      ) : null}
    </div>
  );
}

function readableFileSize(sizeInBytes) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let i = 0;
  let size = sizeInBytes;
  while (size >= 1024) {
    size /= 1024;
    ++i;
  }
  return `${size.toFixed(1)} ${units[i]}`;
}

function fileAttachmentsReducer(prevState, action) {
  const state = deepClone(prevState || []);
  if (action.type === 'add') {
    state.push(action.fileAttachment);
  } else if (action.type === 'remove') {
    const i = state.findIndex((x) => x.index === action.fileAttachment.index);
    state.splice(i, 1);
  } else if (action.type === 'replace') {
    return action.fileAttachments;
  }
  return state;
}

function useValueUpdatedReducer(value, valueUpdated: ValueChangedForFieldFunc) {
  const [currentFileAttachments, setCurrentFileAttachments] = useReducer(fileAttachmentsReducer, null);
  const currentStringValue = currentFileAttachments ? JSON.stringify(currentFileAttachments) : null;
  const hasSkippedInitial = useRef<boolean>(false);
  const callback = useRef<AnyFunc>(null);
  const callbackTimeout = useRef<NodeJS.Timeout>(null);
  const calculatorValue = useRef<TypeMePlease>(value);

  // We want to set isWaitingForValueToUpdate to true for the period of time after we've sent a new value to the calculator
  // and when the new value is available here in this hook. This is to fix concurrency issues
  const isWaitingForValueToUpdate = useRef<TypeMePlease>(false);

  useEffect(() => {
    setCurrentFileAttachments({ type: 'replace', fileAttachments: value ? JSON.parse(value) : [] });
  }, []);

  useEffect(() => {
    if (currentStringValue) {
      if (hasSkippedInitial.current) {
        isWaitingForValueToUpdate.current = true;

        callback.current = (result, attemptNumber) => {
          if (result === calculatorValue.current || attemptNumber > 300) {
            isWaitingForValueToUpdate.current = false;
          } else {
            // value doesn't match the expected result yet, check again in 10ms
            clearTimeout(callbackTimeout.current);
            callbackTimeout.current = setTimeout(() => callback.current(result, attemptNumber + 1), 10);
          }
        };

        valueUpdated({ value: currentStringValue }, (result) => callback.current(result, 1));
      } else {
        hasSkippedInitial.current = true;
      }
    }
  }, [currentStringValue]);

  useEffect(() => {
    calculatorValue.current = value;

    // If isWaitingForValueToUpdate is false then we can ignore this value coming in.
    if (!isWaitingForValueToUpdate.current) {
      setCurrentFileAttachments({ type: 'replace', fileAttachments: value ? JSON.parse(value) : [] });
    }
  }, [value]);

  return [currentFileAttachments || [], setCurrentFileAttachments];
}

function DeleteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.02868 6.98413L13.8317 1.19666C13.9464 1.0631 14.0063 0.89131 13.9995 0.715608C13.9927 0.539906 13.9196 0.373235 13.795 0.248903C13.6703 0.12457 13.5032 0.0517322 13.327 0.0449456C13.1508 0.038159 12.9786 0.0979233 12.8447 0.212295L7.04168 5.99977L1.23868 0.205314C1.10687 0.0738535 0.928092 0 0.74168 0C0.555269 0 0.376493 0.0738535 0.24468 0.205314C0.112868 0.336774 0.0388162 0.515072 0.0388162 0.700984C0.0388162 0.886897 0.112868 1.0652 0.24468 1.19666L6.05468 6.98413L0.24468 12.7716C0.171403 12.8342 0.111889 12.9112 0.0698727 12.9978C0.0278564 13.0844 0.00424528 13.1788 0.000521634 13.2749C-0.00320201 13.3711 0.0130418 13.4669 0.048234 13.5565C0.0834262 13.6461 0.136807 13.7275 0.205025 13.7955C0.273244 13.8636 0.354827 13.9168 0.444654 13.9519C0.534482 13.987 0.630614 14.0032 0.727018 13.9995C0.823421 13.9958 0.918014 13.9722 1.00486 13.9303C1.0917 13.8884 1.16893 13.8291 1.23168 13.756L7.04168 7.96849L12.8447 13.756C12.9786 13.8703 13.1508 13.9301 13.327 13.9233C13.5032 13.9165 13.6703 13.8437 13.795 13.7194C13.9196 13.595 13.9927 13.4284 13.9995 13.2527C14.0063 13.077 13.9464 12.9052 13.8317 12.7716L8.02868 6.98413Z"
        fill="#636363"
      />
    </svg>
  );
}
